import { action, computed, makeObservable, observable } from 'mobx';

import AppSettings from '../AppSettings';
import { IApiClient } from '../data/client';
import { CreateOrderCommand, GetCartQuery, GetOrderQuery, GetShippingMethodsQuery } from '../data/model';
import type {
  GetCartQueryResponse,
  OrderData,
  ShippingRateData,
  ShoppingCartData,
  ShoppingContext,
  StoreContext,
} from '../data/model';
import { CheckoutPageTranslation } from '../localization/SiteTranslation';
import { AppError } from '../shared/AppError';
import { BasePageState } from '../shared/BasePageState';
import { AsyncCommand } from '../shared/common';
import { IEventAggregator } from '../shared/EventAggregator';
import { INavigationService } from '../shared/NavigationService';
import { StatusCodes } from '../shared/StatusCodes';
import { StoreUrl } from '../shared/StoreUrl';
import { StoreState } from '../StoreState';
import { CheckoutFormState } from './CheckoutFormState';
import {
  CheckoutStartedEvent,
  CouponAppliedEvent,
  OrderPlacedEvent,
  PaymentInfoAddedEvent,
  ShippingInfoAddedEvent,
} from './CheckoutPageEvents';
import CheckoutPageQueryParser from './CheckoutPageQueryParser';
import { CheckoutPaymentState } from './CheckoutPaymentState';
import { PaymentSteps } from './CheckoutSteps';
import { DiscountCodeFormState } from './components/DiscountCodeForm/DiscountCodeFormState';
import type { ICheckoutStep } from './ICheckoutStep';
import { PaymentMethodFactory } from './payments/PaymentWidgetFactory';

export class ICheckoutMemento {
  shoppingContext: ShoppingContext;
  storeContext: StoreContext;
  cartResponse: GetCartQueryResponse;
  shippingRates: ShippingRateData[];
  order?: OrderData;
  activeStep?: string;
}

export class CheckoutState extends BasePageState<ICheckoutMemento> {
  public disableCache = true;

  public termsAndConditions: string;
  public legalAgreementsConsentHtml: string;
  private client: IApiClient;
  private navigation: INavigationService;
  private eventAggregator: IEventAggregator;

  public shoppingContext: ShoppingContext;
  public storeContext: StoreContext;
  public cartResponse: GetCartQueryResponse;

  @observable.ref
  public shoppingCart: ShoppingCartData;
  public order: OrderData;

  @observable.ref
  public shippingRates: ShippingRateData[] = [];

  public translation: CheckoutPageTranslation;

  public form: CheckoutFormState;
  public readonly discountCodeForm: DiscountCodeFormState;
  public payment: CheckoutPaymentState;

  public steps: Array<ICheckoutStep> = [];

  public createOrderCommand: AsyncCommand;
  public finalizePaymentCommand: AsyncCommand;
  public currentLanguage: string;

  constructor(
    settings: AppSettings,
    client: IApiClient,
    navigation: INavigationService,
    translation: CheckoutPageTranslation,
    termsAndConditions: string,
    legalAgreementsConsentHtml: string,
    currentLanguage: string,
    eventAggregator: IEventAggregator,
  ) {
    super(translation);

    makeObservable(this);

    this.client = client;
    this.navigation = navigation;
    this.eventAggregator = eventAggregator;
    this.translation = translation;
    this.termsAndConditions = termsAndConditions;
    this.legalAgreementsConsentHtml = legalAgreementsConsentHtml;

    this.discountCodeForm = new DiscountCodeFormState(this.translation.discountCodeForm, this.client);
    this.discountCodeForm.discountCodeAppliedEvent.subscribe(this.onDiscountCodeApplied);

    this.form = new CheckoutFormState(this.translation, [], client);
    this.form.shippingRateChangedEvent.subscribe(this.setCart);

    this.payment = new CheckoutPaymentState(
      this.client,
      new PaymentMethodFactory(termsAndConditions, currentLanguage, this.translation),
      this.translation,
    );
    this.payment.completed.subscribe((args) => {
      this.onComplete(args.redirectUrl, args.paymentOption);
    });

    this.steps = [this.form, this.payment, PaymentSteps.CreateConfirmationStep(this.translation)];
    this.setStep(this.form);

    this.createOrderCommand = new AsyncCommand(() => this.createOrder(), this.form);

    this.onPageLoaded = this.onPageLoaded.bind(this);
    this.navigation.pageLoaded.subscribe(this.onPageLoaded);
  }

  @action
  async onLoad(store: StoreState) {
    const cartResponse = await this.client.send(new GetCartQuery({ shoppingContext: store.shoppingContext }));
    const shippingRatesResponse = await this.client.send(
      new GetShippingMethodsQuery({ shoppingContext: store.shoppingContext }),
    );

    let order: OrderData = null;
    const query = CheckoutPageQueryParser.toModel(this.navigation.currentUrl.query);

    if (query.orderNumber) {
      order = (
        await this.client.send(
          new GetOrderQuery({ orderNumber: query.orderNumber, shoppingContext: store.shoppingContext }),
        )
      ).order;
    }

    this.initialize(
      store.shoppingContext,
      store.storeResponse.storeContext,
      cartResponse,
      shippingRatesResponse.shippingRates,
      order,
      query.step,
    );
  }

  unload() {
    this.payment.disposeCurrentMethod();
    this.navigation.pageLoaded.unsubscribe(this.onPageLoaded);
  }

  private onPageLoaded() {
    if (!this.order) {
      this.notifyCheckoutStarted();
    }
  }

  @action
  private initialize(
    shoppingContext: ShoppingContext,
    storeContext: StoreContext,
    response: GetCartQueryResponse,
    shippingRates: ShippingRateData[],
    order?: OrderData,
    activeStepId?: string,
  ) {
    this.shoppingContext = shoppingContext;
    this.storeContext = storeContext;
    this.cartResponse = response;
    this.shippingRates = shippingRates;
    this.order = order;

    this.setCart(response.shoppingCart);

    this.form.initialize(storeContext, shoppingContext, shippingRates, response.shoppingCart.shipments[0]);
    this.payment.initialize(shoppingContext, storeContext);
    this.discountCodeForm.initialize(shoppingContext);

    if (order && activeStepId) {
      const activeStep = this.steps.find((x) => x.id === activeStepId);

      if (activeStep) {
        this.steps.forEach((step) => {
          step.restoreOrder(order);

          if (step === activeStep) {
            this.setStep(step);
            return;
          }
        });
      }
    }
  }

  get activeDiscountCode() {
    return this.shoppingCart.appliedCoupons.first();
  }

  get isFormSubmitButtonDisabled() {
    return this.form.recalculating || this.discountCodeForm.isProcessing;
  }

  @computed
  get shippingCostAvailable() {
    return this.shoppingCart.shipments.length > 0 || this.shippingRates.length === 0;
  }

  getMemento() {
    const activeStep = this.steps.find((x) => x.isActive);

    let memento: ICheckoutMemento = {
      shoppingContext: this.shoppingContext,
      storeContext: this.storeContext,
      cartResponse: this.cartResponse,
      shippingRates: this.shippingRates,
      order: this.order,
      activeStep: activeStep ? activeStep.id : null,
    };

    return memento;
  }

  restoreMemento(memento: ICheckoutMemento) {
    this.initialize(
      memento.shoppingContext,
      memento.storeContext,
      memento.cartResponse,
      memento.shippingRates,
      memento.order,
      memento.activeStep,
    );
  }

  @action
  async createOrder() {
    const paymentMethodCode = this.form.selectedPaymentMethod.code;
    this.payment.setMethod(paymentMethodCode);

    const command = new CreateOrderCommand({
      shoppingContext: this.shoppingContext,
      contactAndBilling: this.form.getContactAndBilling(),
      deliveryAddress: this.form.getDeliveryAddress(),
      paymentMethodCode: paymentMethodCode,
      parameters: this.payment.getInitialParameters(),
      // TODO: Pass a discount code on order creation
      // discountCode: this.discountCode.value,
    });

    const response = await this.client.send(command);

    if (response.paymentOperationResult.isSuccess) {
      this.order = response.order;
      await this.payment.processOrder(response.order, response.paymentOperationResult.parameters);
      this.notifyShippingInfoAdded();
      this.setStep(this.payment);
    } else {
      throw new AppError(
        this.translation.errors.initializingPaymentFailed,
        `Payment initialization failed, order id = ${response.order.id}, error = ${response.paymentOperationResult.error}}`,
      );
    }
  }

  @action.bound
  backToPreviousPage() {
    this.navigation.back();
  }

  @action.bound
  editData() {
    this.setStep(this.form);
  }

  @action.bound setStep(step: ICheckoutStep) {
    this.steps.forEach((x) => (x.isActive = x === step));
  }

  @action.bound
  private setCart(cart: ShoppingCartData) {
    this.shoppingCart = cart;
  }

  @action.bound
  private onDiscountCodeApplied(cart: ShoppingCartData) {
    this.setCart(cart);
    this.notifyCouponApplied();
  }

  private onComplete(redirectUrl?: string, selectedPaymentOption?: string) {
    if (selectedPaymentOption) {
      this.notifyPaymentInfoAdded(selectedPaymentOption);
    }

    this.notifyOrderPlaced();

    if (redirectUrl) {
      this.navigation.redirect(redirectUrl, StatusCodes.MovedTemporary);
    } else {
      this.navigation.navigateTo(StoreUrl.confirmationUrl(this.order.number));
    }
  }

  private notifyCheckoutStarted() {
    this.eventAggregator.publish(new CheckoutStartedEvent(this.shoppingCart, this.activeDiscountCode));
  }

  private notifyCouponApplied() {
    this.eventAggregator.publish(new CouponAppliedEvent(this.shoppingCart, this.activeDiscountCode));
  }

  private notifyShippingInfoAdded() {
    this.eventAggregator.publish(new ShippingInfoAddedEvent(this.shoppingCart, this.activeDiscountCode));
  }

  private notifyPaymentInfoAdded(paymentType: string) {
    this.eventAggregator.publish(new PaymentInfoAddedEvent(this.shoppingCart, this.activeDiscountCode, paymentType));
  }

  private notifyOrderPlaced() {
    this.eventAggregator.publish(new OrderPlacedEvent(this.order, this.activeDiscountCode));
  }
}
